import { useQuery } from 'react-query';
import { useState } from 'react';
import Header from '@/components/Header';
import LoadingSpinner from '@/components/LoadingSpinner';
import { apiClient } from '@/api/client';
import type { PricingPlan } from '@/types';
import { ROUTES } from '@/utils/constants';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import toast from 'react-hot-toast';

const PricingPage = () => {
  const { data: plans, isLoading, error } = useQuery('pricing', () => apiClient.getPricing(), {
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <div className="main-page-wrapper">
        <Header />
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-page-wrapper">
        <Header />
        <div className="container pt-150 lg-pt-80 pb-100 lg-pb-80 mt-30 lg-mt-20">
          <div className="text-center">
            <p className="text-danger">Failed to load pricing. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-page-wrapper">
      <Header />
      
      <section className="pricing-section-one pt-150 lg-pt-80 pb-100 lg-pb-80 mt-30 lg-mt-20">
        <div className="container">
          <div className="text-center mb-60 lg-mb-40">
            <div className="title-one">
              <h2>Simple, Transparent Pricing</h2>
            </div>
            <p className="fs-20 text-dark mt-20">
              Choose the plan that fits your needs. All prices in Nigerian Naira (NGN).
            </p>
          </div>

          {plans && plans.length > 0 ? (
            <div className="row gx-xl-5">
              {plans.map((plan) => (
                <PricingCard key={plan.plan} plan={plan} />
              ))}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-dark">No pricing plans available at the moment.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const PricingCard = ({ plan }: { plan: PricingPlan }) => {
  const { firebaseUser, userProfile } = useAuthStore();
  const navigate = useNavigate();
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  const planTier = plan.plan || 'free';
  const isPopular = planTier === 'professional';
  const isFree = planTier === 'free';
  const planName = plan.features?.name || planTier.charAt(0).toUpperCase() + planTier.slice(1);
  const monthlyFee = plan.monthly_fee_display || '₦0';
  const pricePer1k = plan.price_per_1k_chars_display || 'Free';
  
  const isLoggedIn = !!firebaseUser;
  const currentPlan = userProfile?.plan || 'free';
  const isCurrentPlan = currentPlan === planTier;
  
  const handleUpgrade = async () => {
    if (!isLoggedIn) {
      toast('Please sign up or log in first', { icon: '👋' });
      navigate(ROUTES.SIGNUP);
      return;
    }
    
    if (isFree) {
      // Already on free, go to dashboard
      navigate(ROUTES.DASHBOARD);
      return;
    }
    
    setIsUpgrading(true);
    try {
      // Initialize Paystack payment
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/payments/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: firebaseUser?.email,
          plan: planTier,
          user_id: firebaseUser?.uid,
          callback_url: `${window.location.origin}/dashboard?payment=success&plan=${planTier}`,
        }),
      });
      
      const data = await response.json();
      
      if (data.authorization_url) {
        // Redirect to Paystack checkout
        toast.success('Redirecting to payment...');
        window.location.href = data.authorization_url;
      } else {
        toast.error(data.detail || 'Failed to initialize payment');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to process upgrade');
    } finally {
      setIsUpgrading(false);
    }
  };
  
  const getButtonText = () => {
    if (isCurrentPlan) return 'Current Plan';
    if (isFree && isLoggedIn) return 'Current Plan';
    if (isFree) return 'Get Started Free';
    if (isLoggedIn) return `Upgrade to ${planName}`;
    return 'Get Started';
  };
  
  const getButtonAction = () => {
    if (isCurrentPlan || (isFree && isLoggedIn)) return () => navigate(ROUTES.DASHBOARD);
    if (!isLoggedIn) return () => navigate(ROUTES.SIGNUP);
    return handleUpgrade;
  };
  
  return (
    <div className={`col-lg-4 col-md-6 mt-30 lg-mt-20`}>
      <div className={`pricing-card-one text-center border-30 bg-white h-100 position-relative ${isPopular ? 'popular' : ''} ${isCurrentPlan ? 'border-success' : isFree ? 'border-primary' : ''}`} style={{ 
        borderWidth: isCurrentPlan ? '3px' : isFree ? '2px' : '1px',
        borderStyle: 'solid',
        padding: '40px 30px',
      }}>
        {isPopular && !isCurrentPlan && (
          <span className="tag">Most Popular</span>
        )}
        {isCurrentPlan && (
          <span className="tag" style={{ backgroundColor: '#28a745', color: 'white' }}>✓ Your Plan</span>
        )}
        {isFree && !isCurrentPlan && !isLoggedIn && (
          <span className="tag" style={{ backgroundColor: '#28a745', color: 'white' }}>Free Forever</span>
        )}
        <div className="pack-name" style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>
          {planName}
        </div>
        <div className="price fw-500 mb-3">
          <span className="currency" style={{ fontSize: '48px', fontWeight: '700' }}>
            {monthlyFee}
          </span>
          {!isFree && <span className="fs-16">/month</span>}
        </div>
        <p className="fs-16 mt-10 mb-30" style={{ color: '#666' }}>
          {pricePer1k} per 1,000 chars
        </p>
        <ul className="style-none text-start mb-40" style={{ paddingLeft: '20px' }}>
          <li className="mb-3 d-flex align-items-start">
            <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
            <span>{plan.features?.characters_per_month || 'N/A'} characters/month</span>
          </li>
          <li className="mb-3 d-flex align-items-start">
            <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
            <span>{plan.features?.requests_per_minute || 'N/A'} requests/minute</span>
          </li>
          <li className="mb-3 d-flex align-items-start">
            <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
            <span>{plan.features?.voices || 'All Nigerian voices'}</span>
          </li>
          {plan.features?.usage_analytics && (
            <li className="mb-3 d-flex align-items-start">
              <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
              <span>Usage Analytics</span>
            </li>
          )}
          {plan.features?.custom_integration && (
            <li className="mb-3 d-flex align-items-start">
              <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
              <span>Custom Integration</span>
            </li>
          )}
          {plan.features?.sla && (
            <li className="mb-3 d-flex align-items-start">
              <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
              <span>{plan.features.sla}</span>
            </li>
          )}
        </ul>
        <button 
          onClick={getButtonAction()}
          disabled={isUpgrading || isCurrentPlan}
          className={`btn-one w-100 mt-auto ${isCurrentPlan ? 'bg-success' : isPopular ? '' : isFree ? 'bg-success' : 'border'}`}
          style={{ 
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isCurrentPlan ? 'default' : 'pointer',
            opacity: isCurrentPlan ? 0.8 : 1,
          }}
        >
          {isUpgrading ? 'Processing...' : getButtonText()}
        </button>
      </div>
    </div>
  );
};

export default PricingPage;

